/**
 * Frontend HTTP client for the local Express/Drizzle API.
 * Replaces all direct Supabase client calls.
 *
 * JWT access token is stored in memory (module-level variable).
 * Refresh token lives in an httpOnly cookie managed by the server.
 */

let accessToken = null;

export function setAccessToken(token) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

export function clearAccessToken() {
  accessToken = null;
}

async function refreshAccessToken() {
  const res = await fetch('/api/auth/refresh', {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    clearAccessToken();
    return null;
  }
  const data = await res.json();
  setAccessToken(data.access_token);
  return data.access_token;
}

async function request(method, path, body, options = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const res = await fetch(path, {
    method,
    headers,
    credentials: 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined,
    ...options,
  });

  // 401 → try to refresh once
  if (res.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`;
      const retry = await fetch(path, {
        method,
        headers,
        credentials: 'include',
        body: body !== undefined ? JSON.stringify(body) : undefined,
        ...options,
      });
      if (!retry.ok) {
        const err = await retry.json().catch(() => ({ error: retry.statusText }));
        throw new Error(err.error || `HTTP ${retry.status}`);
      }
      if (retry.status === 204) return null;
      return retry.json();
    }
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

export const api = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body),
  put: (path, body) => request('PUT', path, body),
  patch: (path, body) => request('PATCH', path, body),
  delete: (path, body) => request('DELETE', path, body),
};

// ---------------------------------------------------------------------------
// Auth helpers
// ---------------------------------------------------------------------------

export async function apiSignIn(email, password) {
  const data = await api.post('/api/auth/login', { email, password });
  setAccessToken(data.access_token);
  return data;
}

export async function apiSignInWithMagicLink(email) {
  return api.post('/api/auth/magic-link', { email });
}

export async function apiVerifyOtp(token) {
  const data = await api.post('/api/auth/verify-otp', { token });
  setAccessToken(data.access_token);
  return data;
}

export async function apiSignOut() {
  await api.post('/api/auth/logout').catch(() => {});
  clearAccessToken();
}

export async function apiGetMe() {
  return api.get('/api/auth/me');
}

export async function apiGetUserRoles(userId) {
  return api.get(`/api/user-roles?user_id=${userId}`);
}

// ---------------------------------------------------------------------------
// Thin compatibility wrapper that mimics the Supabase client's query API.
// Components that were refactored can keep using `.from().select()` etc.,
// but they now call the REST API instead of Supabase.
//
// IMPORTANT: This shim is intentionally minimal. It handles the most common
// Supabase query patterns. Complex joins that used Supabase's PostgREST syntax
// should be converted to use `api.get/post/patch/delete` directly.
// ---------------------------------------------------------------------------

class QueryBuilder {
  constructor(table) {
    this._table = table;
    this._method = 'GET';
    this._body = null;
    this._filters = {};
    this._select = '*';
    this._order = null;
    this._limit = null;
    this._single = false;
    this._maybeSingle = false;
  }

  select(fields = '*') {
    this._select = fields;
    return this;
  }

  insert(values) {
    this._method = 'POST';
    this._body = Array.isArray(values) ? values[0] : values;
    return this;
  }

  update(values) {
    this._method = 'PATCH';
    this._body = values;
    return this;
  }

  delete() {
    this._method = 'DELETE';
    return this;
  }

  upsert(values, opts) {
    this._method = 'POST';
    this._body = Array.isArray(values) ? values[0] : values;
    this._upsert = true;
    return this;
  }

  eq(col, val) { this._filters[col] = val; return this; }
  neq(col, val) { this._filters[`${col}[neq]`] = val; return this; }
  in(col, vals) { this._filters[`${col}[in]`] = vals.join(','); return this; }
  not(col, op, val) {
    if (op === 'is' && val === null) this._filters[`${col}[not]`] = 'null';
    return this;
  }
  gte(col, val) { this._filters[`${col}[gte]`] = val; return this; }
  is(col, val) {
    if (val === null) this._filters[`${col}[is]`] = 'null';
    else this._filters[`${col}[is]`] = val;
    return this;
  }
  or(expr) { this._filters['_or'] = expr; return this; }
  order(col, opts = {}) { this._order = { col, ...opts }; return this; }
  limit(n) { this._limit = n; return this; }
  single() { this._single = true; return this; }
  maybeSingle() { this._maybeSingle = true; return this; }

  async then(resolve) {
    // Always resolve (never reject) — mirrors Supabase client behaviour where
    // errors are returned as { data: null, error } rather than thrown.
    try {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(this._filters)) {
        params.set(k, v);
      }
      if (this._order) {
        params.set('_order', `${this._order.col}:${this._order.ascending === false ? 'desc' : 'asc'}`);
      }
      if (this._limit) params.set('_limit', this._limit);

      const qs = params.toString() ? `?${params}` : '';

      let raw;
      if (this._method === 'GET') {
        raw = await api.get(`/api/${this._table}${qs}`);
      } else if (this._method === 'POST') {
        raw = await api.post(`/api/${this._table}`, this._body);
      } else if (this._method === 'PATCH') {
        const id = this._filters.id;
        raw = await api.patch(`/api/${this._table}/${id}`, this._body);
      } else if (this._method === 'DELETE') {
        const id = this._filters.id;
        await api.delete(`/api/${this._table}/${id}`);
        raw = null;
      }

      let data = raw;
      if (this._single || this._maybeSingle) {
        data = Array.isArray(raw) ? (raw[0] || null) : raw;
      }

      resolve({ data, error: null });
    } catch (err) {
      resolve({ data: null, error: err });
    }
  }
}

export const supabaseCompat = {
  from: (table) => new QueryBuilder(table),
  rpc: async (fn, params) => {
    try {
      const data = await api.post(`/api/rpc/${fn}`, params);
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  },
  auth: {
    getSession: async () => {
      if (!accessToken) return { data: { session: null }, error: null };
      try {
        const user = await apiGetMe();
        return { data: { session: { user, access_token: accessToken } }, error: null };
      } catch {
        return { data: { session: null }, error: null };
      }
    },
    onAuthStateChange: () => {
      // No-op; auth state is managed by AuthContext polling
      return { data: { subscription: { unsubscribe: () => {} } } };
    },
    signInWithOtp: async ({ email }) => {
      await apiSignInWithMagicLink(email);
      return { data: {}, error: null };
    },
    signOut: async () => {
      await apiSignOut();
      return { error: null };
    },
  },
  channel: () => {
    const noopSub = { unsubscribe: () => {} };
    const chan = {
      on: () => chan,
      subscribe: () => noopSub,
      unsubscribe: () => {},
    };
    return chan;
  },
  removeChannel: () => {},
};
